import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const ImageCropperModal = ({
  open,
  file,
  title = "Crop image",
  aspect = 1,
  outputWidth,
  outputHeight,
  onCancel,
  onCropped,
}) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const objectUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    if (open) {
      setCrop(undefined);
      setCompletedCrop(null);
      setIsSaving(false);
    }
  }, [open]);

  const onImageLoad = (e) => {
    const img = e.currentTarget;
    imgRef.current = img;
    if (!aspect) return;

    const { width, height } = img;
    const next = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          // Start with the largest possible crop (for 1:1, this becomes a big square)
          width: 100,
        },
        aspect,
        width,
        height
      ),
      width,
      height
    );
    setCrop(next);
  };

  const handleOk = async () => {
    if (!file || !imgRef.current || !completedCrop?.width || !completedCrop?.height) return;
    setIsSaving(true);
    try {
      const img = imgRef.current;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not get canvas context");

      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const sx = completedCrop.x * scaleX;
      const sy = completedCrop.y * scaleY;
      const sWidth = completedCrop.width * scaleX;
      const sHeight = completedCrop.height * scaleY;

      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, outputWidth, outputHeight);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => {
            if (!b) {
              reject(new Error("Canvas is empty"));
              return;
            }
            resolve(b);
          },
          "image/jpeg",
          0.95
        );
      });

      const safeName = file.name ? file.name.replace(/\.[^/.]+$/, "") : "image";
      const croppedFile = new File([blob], `${safeName}-cropped.jpg`, { type: blob.type || "image/jpeg" });
      onCropped?.(croppedFile);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Crop"
      cancelText="Cancel"
      confirmLoading={isSaving}
      width={720}
      destroyOnClose
    >
      <div style={{ width: "100%", maxHeight: 520, overflow: "auto" }}>
        {objectUrl && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            keepSelection
            minWidth={20}
            minHeight={20}
          >
            <img
              alt="Crop source"
              src={objectUrl}
              onLoad={onImageLoad}
              style={{ maxWidth: "100%", maxHeight: 500, display: "block" }}
            />
          </ReactCrop>
        )}
      </div>
    </Modal>
  );
};

export default ImageCropperModal;

